import React from 'react';
import { Pencil } from 'lucide-react';
import PropTypes from 'prop-types';

import { formatCurrency } from '../../../../utils/formatters';
import {
  accountTypeOptions,
  areaLabels,
  companyTypeOptions,
  formatBankCode,
  formatDate,
  formatDocument,
  formatPercentage,
  formatPhoneNumber,
  formatPostalCode,
  jobTitleLabels,
  optionLabel,
  profileArea,
  statusLabels,
  transferIntervalOptions,
} from './accountConstants';

const Detail = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="text-xs font-semibold text-tealExtraDark">{label}</dt>
    <dd className="mt-1 break-words text-sm text-blueDarker">
      {value === null || value === undefined || value === '' ? '—' : value}
    </dd>
  </div>
);

Detail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Detail.defaultProps = {
  value: '—',
};

const SectionTitle = ({ children }) => (
  <h3 className="mb-4 text-xs font-semibold uppercase tracking-wide text-blueMedium">{children}</h3>
);

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

const AccountView = ({ employee, activeTab, onClose, onEdit }) => {
  const profile = employee.accessProfile;
  const bankAccount = employee.bankAccount || {};

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex-1 py-6">
        {activeTab === 'personal' && (
          <div className="space-y-6">
            <section>
              <SectionTitle>Dados pessoais</SectionTitle>
              <dl className="grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                <Detail label="Nome" value={employee.fullName} />
                <Detail label="E-mail" value={employee.email} />
                <Detail label="CPF" value={formatDocument(employee.cpf)} />
                <Detail label="Data de nascimento" value={formatDate(employee.birthDate)} />
                <Detail label="Telefone" value={formatPhoneNumber(employee.phone)} />
                <Detail
                  label="Faturamento/Renda mensal"
                  value={
                    employee.incomeValue === null || employee.incomeValue === undefined
                      ? '—'
                      : formatCurrency(employee.incomeValue)
                  }
                />
              </dl>
            </section>

            <section>
              <SectionTitle>Acesso</SectionTitle>
              <dl className="grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                <Detail label="Área" value={areaLabels[profileArea(profile)] || '—'} />
                <Detail label="Cargo" value={jobTitleLabels[profile?.jobTitle] || profile?.name || '—'} />
                <Detail label="Status" value={statusLabels[employee.status] || employee.status} />
              </dl>
            </section>

            <section>
              <SectionTitle>Histórico</SectionTitle>
              <dl className="grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                <Detail label="Data de cadastro" value={formatDate(employee.createdAt)} />
                <Detail label="Último acesso" value={formatDate(employee.lastSignInAt)} />
              </dl>
            </section>
          </div>
        )}

        {activeTab === 'address' && (
          <div className="space-y-6">
            <section>
              <SectionTitle>Endereço</SectionTitle>
              <dl className="grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                <Detail label="CEP" value={formatPostalCode(employee.address?.postalCode)} />
                <Detail label="Logradouro" value={employee.address?.street} />
                <Detail label="Número" value={employee.address?.number} />
                <Detail label="Bairro" value={employee.address?.province} />
                <Detail label="Cidade" value={employee.address?.city} />
                <Detail label="UF" value={employee.address?.state} />
              </dl>
            </section>

            <section>
              <SectionTitle>Comissão</SectionTitle>
              <dl className="grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                <Detail label="Comissão Pgto Recorrente" value={formatPercentage(employee.commission?.recurrent)} />
                <Detail label="Comissão Pgto Inscrição" value={formatPercentage(employee.commission?.subscription)} />
              </dl>
            </section>
          </div>
        )}

        {activeTab === 'bank' && (
          <section>
            <SectionTitle>Dados bancários · Repasse de comissão</SectionTitle>
            <dl className="grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
              <Detail label="Nome completo (titular)" value={bankAccount.name} />
              <Detail label="CPF/CNPJ" value={formatDocument(bankAccount.documentNumber)} />
              <Detail label="Company type" value={optionLabel(companyTypeOptions, bankAccount.companyType)} />
              <Detail
                label="Intervalo de transferência"
                value={optionLabel(transferIntervalOptions, bankAccount.transferInterval)}
              />
              <Detail label="Dia da transferência" value={bankAccount.transferDay} />
              <Detail label="Tipo de conta" value={optionLabel(accountTypeOptions, bankAccount.accountType)} />
              <Detail label="Cód. do banco" value={formatBankCode(bankAccount.bankCode)} />
              <Detail label="Agência" value={bankAccount.bankAg} />
              <Detail label="Dígito verif. da agência" value={bankAccount.agenciaDv} />
              <Detail label="Nº da conta" value={bankAccount.bankCc} />
              <Detail label="Dígito verif. da conta" value={bankAccount.contaDv} />
            </dl>
          </section>
        )}
      </div>

      <footer className="mt-5 flex justify-end gap-5">
        <button
          type="button"
          onClick={onClose}
          style={{ minWidth: '102px' }}
          className="rounded-xl border-b-2 border-r-2 border-graySemiDark bg-blueUltraLight px-6 py-3 text-sm font-semibold text-grayDark transition-colors hover:bg-grayLight"
        >
          FECHAR
        </button>
        <button
          type="button"
          onClick={onEdit}
          style={{ minWidth: '122px' }}
          className="inline-flex items-center justify-center gap-2 rounded-xl border-b-2 border-r-2 border-tealBase bg-tealMedium px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          <Pencil size={17} strokeWidth={1.8} />
          EDITAR
        </button>
      </footer>
    </div>
  );
};

AccountView.propTypes = {
  employee: PropTypes.shape({
    fullName: PropTypes.string,
    email: PropTypes.string,
    cpf: PropTypes.string,
    birthDate: PropTypes.string,
    phone: PropTypes.string,
    incomeValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    role: PropTypes.string,
    status: PropTypes.string,
    accessProfile: PropTypes.object,
    address: PropTypes.object,
    commission: PropTypes.object,
    bankAccount: PropTypes.object,
    createdAt: PropTypes.string,
    lastSignInAt: PropTypes.string,
  }).isRequired,
  activeTab: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default AccountView;
